/**
 * The error contract every 4xx that reports a caller's mistake now arrives in
 * (backend #682 decision 2).
 *
 * ```json
 * { "status": 400, "title": "One or more validation errors occurred.",
 *   "detail": "…developer text…",
 *   "errors": [ { "code": "notEmpty", "field": "Salutation", "params": null, "message": "…" } ],
 *   "traceId": "00-…" }
 * ```
 *
 * The point of it is that **the server stopped sending prose for a client to display.** It used
 * to send German `UserFriendlyErrorMessage`s and English developer strings interchangeably —
 * „The Address field is required.", „Cannot create blob without product ID" — which is why
 * `CallApiService` suppresses server text altogether. That suppression stays; what changes is
 * that there is now something better than a generic toast to put in its place.
 */
export interface ApiError {
  /** A backend-wide camelCase code: a normalized FluentValidation rule name, or an `ErrorCodes` constant. */
  code: string;
  /** The request property, as the client sent it. `null` for a rule about the whole request. */
  field?: string | null;
  /** The rule's placeholders — `maxLength`, `comparisonValue`, `ids`, … — camelCased. */
  params?: Record<string, unknown> | null;
  /** English developer text. **Never shown to a user.** */
  message?: string;
}

export interface ApiProblem {
  status?: number;
  title?: string;
  detail?: string;
  errors?: ApiError[];
  traceId?: string;
  [extension: string]: unknown;
}

/**
 * The problem-details body out of an `HttpErrorResponse`.
 *
 * The BFF forwards a domain API's body verbatim, which on some paths arrives double-encoded (a
 * JSON string rather than an object), so both are handled. Anything that is not a problem body
 * comes back undefined rather than half-parsed.
 *
 * `T` names the extension members a caller relies on — the 409's `pendingOrderGroupId`, the
 * order gate's `positions` (#199: two components used to keep their own copy of this parser to
 * reach them). It is a statement about the endpoint, not a check of the body.
 */
export function parseApiProblem<T extends object = ApiProblem>(err: unknown): T | undefined {
  const body = (err as { error?: unknown })?.error;
  if (body == null) {
    return undefined;
  }
  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      return typeof parsed === 'object' && parsed !== null ? (parsed as T) : undefined;
    } catch {
      return undefined;
    }
  }
  return typeof body === 'object' ? (body as T) : undefined;
}

/**
 * The coded errors an `HttpErrorResponse` carries, or `[]`.
 *
 * Tolerates the two shapes that predate the contract, because not every path is on it yet and a
 * client that only understood the new one would report nothing at all for the others: the old
 * `errors: string[]` extension, and ASP.NET's `errors: { field: string[] }` ModelState map. Both
 * degrade to `{ code: 'validation', message }` — no dictionary entry, so they land on the
 * caller's generic toast exactly as they do today.
 */
export function apiErrors(err: unknown): ApiError[] {
  const errors = parseApiProblem(err)?.errors as unknown;

  if (Array.isArray(errors)) {
    return errors
      .map((entry) =>
        typeof entry === 'string'
          ? ({ code: 'validation', message: entry } as ApiError)
          : (entry as ApiError),
      )
      .filter((entry) => !!entry?.code);
  }

  if (errors && typeof errors === 'object') {
    return Object.entries(errors as Record<string, unknown>).flatMap(([field, messages]) =>
      (Array.isArray(messages) ? messages : [messages])
        .filter((m): m is string => typeof m === 'string')
        .map((message) => ({ code: 'validation', field, message }) as ApiError),
    );
  }

  return [];
}

/** The trace id to quote in a bug report, when the body carries one. */
export function apiTraceId(err: unknown): string | undefined {
  const traceId = parseApiProblem(err)?.traceId;
  return typeof traceId === 'string' ? traceId : undefined;
}
